/**
 * Roteador mínimo por hash (`#/dashboard`), suficiente para uma SPA de
 * navegação modular sem exigir configuração de fallback de histórico no
 * servidor de produção (ainda indefinido — `DEPLOY.md`, seção 15). Sem
 * dependência externa, por decisão consciente de manter o frontend sem
 * dependências desnecessárias (`ARCHITECTURE_GUIDE.md`, seção 8).
 *
 * A lógica pura de normalização/casamento de rota (`normalizarHash`,
 * `encontrarRota`) é separada da orquestração de DOM (`Router`) para
 * poder ser testada sem precisar de um `window` real.
 */
export interface Rota {
  caminho: string;
  /** `true` = acessível sem sessão (ex: login). Padrão: exige sessão. */
  publica?: boolean;
  renderizar: () => HTMLElement | Promise<HTMLElement>;
}

export function normalizarHash(hash: string): string {
  const semPrefixo = hash.startsWith('#') ? hash.slice(1) : hash;
  if (semPrefixo.length === 0) return '/';
  const semBarraFinal =
    semPrefixo.length > 1 && semPrefixo.endsWith('/') ? semPrefixo.slice(0, -1) : semPrefixo;
  return semBarraFinal.startsWith('/') ? semBarraFinal : `/${semBarraFinal}`;
}

export function encontrarRota(rotas: readonly Rota[], caminho: string): Rota | null {
  return rotas.find((rota) => rota.caminho === caminho) ?? null;
}

export interface RouterConfig {
  rotas: readonly Rota[];
  outlet: HTMLElement;
  caminhoLogin: string;
  caminhoInicial: string;
  temSessao: () => boolean;
  renderizarNaoEncontrada: () => HTMLElement;
  /** Chamado após cada renderização bem-sucedida, com a rota resolvida — usado para sincronizar título/sidebar ativa. */
  aoRenderizar?: (rota: Rota) => void;
}

export class Router {
  private renderizando: Promise<void> = Promise.resolve();

  constructor(private readonly config: RouterConfig) {}

  iniciar(): void {
    window.addEventListener('hashchange', () => this.agendarRenderizacao());
    this.agendarRenderizacao();
  }

  navegar(caminho: string): void {
    if (normalizarHash(window.location.hash) === caminho) {
      void this.agendarRenderizacao();
      return;
    }
    window.location.hash = `#${caminho}`;
  }

  /** Serializa renderizações — evita duas trocas de hash em sequência rápida disputarem o mesmo `outlet`. */
  private agendarRenderizacao(): Promise<void> {
    this.renderizando = this.renderizando.then(() => this.renderizarAtual());
    return this.renderizando;
  }

  private async renderizarAtual(): Promise<void> {
    const caminho = normalizarHash(window.location.hash);
    const autenticado = this.config.temSessao();

    if (caminho === '/') {
      this.navegar(autenticado ? this.config.caminhoInicial : this.config.caminhoLogin);
      return;
    }

    const rota = encontrarRota(this.config.rotas, caminho);

    if (rota?.caminho === this.config.caminhoLogin && autenticado) {
      this.navegar(this.config.caminhoInicial);
      return;
    }

    // Rota inexistente é tratada com a mesma exigência de sessão de uma
    // rota protegida comum — nunca deixa um visitante anônimo ver
    // sequer o estado "não encontrada" antes de autenticar.
    if ((rota ? !rota.publica : true) && !autenticado) {
      this.navegar(this.config.caminhoLogin);
      return;
    }

    const elemento = rota ? await rota.renderizar() : this.config.renderizarNaoEncontrada();
    this.config.outlet.replaceChildren(elemento);

    if (rota) this.config.aoRenderizar?.(rota);
  }
}
